import {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import {getBrowserLanguage, isLanguageAvailable} from './config/languages';
import {logDebug} from './utils/log';

// Cache globale per le traduzioni precaricate
window.__PRELOADED_TRANSLATIONS__ = {};

// Espone nome e versione app su window per Tampermonkey/Greasemonkey
(window as unknown as Record<string, unknown>)['__APP__NAME__'] = __APP_NAME__;
(window as unknown as Record<string, unknown>)['__APP__VERSION__'] = __APP_VERSION__;

// Funzione per preload delle traduzioni ottimizzata
function preloadTranslations() {
  // Determina la lingua da caricare
  const savedLang = localStorage.getItem('selectedLanguage');
  let langCode: string;
  
  if (savedLang && isLanguageAvailable(savedLang)) {
    langCode = savedLang;
  } else {
    const browserLang = getBrowserLanguage();
    langCode = isLanguageAvailable(browserLang) ? browserLang : 'it_IT';
  }

  // Usa requestIdleCallback per non bloccare il main thread
  const loadWhenIdle = (callback: () => void) => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout: 1000 });
    } else {
      setTimeout(callback, 0);
    }
  };

  loadWhenIdle(async () => {
    try {
      logDebug(`🌐 Preload traduzioni per: ${langCode}`);
      
      const fileName = `/${langCode}.json`;
      const response = await fetch(fileName);
      
      if (response.ok) {
        window.__PRELOADED_TRANSLATIONS__[langCode] = await response.json();
        logDebug(`✅ Traduzioni precaricate per ${langCode}`);
        
        // Trigger re-render del LanguageProvider se necessario
        window.dispatchEvent(new CustomEvent('translationsLoaded', { detail: langCode }));
      } else {
        throw new Error(`Failed to fetch ${fileName}`);
      }
    } catch (error) {
      logDebug('❌ Errore nel preload traduzioni:', error);
      // Fallback silenzioso
      try {
        const itModule = await import('../it_IT.json');
        window.__PRELOADED_TRANSLATIONS__['it_IT'] = itModule.default;
        logDebug('✅ Fallback: traduzioni italiane caricate');
        window.dispatchEvent(new CustomEvent('translationsLoaded', { detail: 'it_IT' }));
      } catch (fallbackError) {
        logDebug('❌ Errore anche nel fallback:', fallbackError);
      }
    }
  });
}

// CSS critico per prevenire CLS immediato
const criticalCSS = `
  /* CLS Prevention - Immediate Load */
  * { box-sizing: border-box !important; }

  .text-4xl, .text-5xl, .text-6xl {
    min-height: 4rem !important;
    line-height: 1.1 !important;
  }

  .text-3xl {
    min-height: 3rem !important;
    line-height: 1.2 !important;
  }

  p, .text-lg {
    min-height: 2rem !important;
  }
`;

// Inietta CSS critico immediatamente
const criticalStyle = document.createElement('style');
criticalStyle.textContent = criticalCSS;
document.head.appendChild(criticalStyle);

// Avvia preload in background (non bloccante)
preloadTranslations();

// Renderizza immediatamente l'app
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);

